import React, { useCallback } from 'react';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Icon } from '@liftango/ui-icons';
import type { ImportFileEntry, ImportFileType } from '../types';

interface FileUploadStepProps {
  files: ImportFileEntry[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (fileId: string) => void;
  onSetFileType: (fileId: string, type: ImportFileType) => void;
}

const FILE_TYPE_OPTIONS: { value: ImportFileType; labelKey: string }[] = [
  { value: 'rides', labelKey: 'importWizard.fileTypes.rides.label' },
  { value: 'points', labelKey: 'importWizard.fileTypes.points.label' },
  { value: 'polygons', labelKey: 'importWizard.fileTypes.polygons.label' },
  { value: 'fleet_shifts', labelKey: 'importWizard.fileTypes.fleet_shifts.label' },
  { value: 'gtfs', labelKey: 'importWizard.fileTypes.gtfs.label' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FileUploadStep: React.FC<FileUploadStepProps> = ({
  files,
  onAddFiles,
  onRemoveFile,
  onSetFileType,
}) => {
  const { t } = useTranslation('mapDialogs');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onAddFiles(acceptedFiles);
    }
  }, [onAddFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/zip': ['.zip'],
      'application/vnd.google-earth.kml+xml': ['.kml'],
    },
    multiple: true,
    maxSize: 50 * 1024 * 1024,
  });

  return (
    <Stack spacing={3}>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <Icon name="upload" size={48} />
        <Typography variant="body1" sx={{ mt: 1 }}>
          {isDragActive
            ? t('importWizard.uploadStep.dropHere')
            : t('importWizard.uploadStep.dragOrClick')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('importWizard.uploadStep.supportedFormats')}
        </Typography>
      </Box>

      {files.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">
            {t('importWizard.uploadStep.uploadedFiles', { count: files.length })}
          </Typography>
          {files.map(file => (
            <Box
              key={file.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: file.parseError ? 'error.main' : 'divider',
                borderRadius: 1,
                backgroundColor: file.parseError ? 'error.lighter' : 'background.paper',
              }}
            >
              <Icon name="file-import" size={20} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap fontWeight={500}>
                  {file.fileName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(file.fileSize)}
                  {file.parsedRows.length > 0 && ` · ${file.parsedRows.length} rows`}
                </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t('importWizard.uploadStep.fileType')}</InputLabel>
                <Select
                  value={file.fileType}
                  label={t('importWizard.uploadStep.fileType')}
                  onChange={e => onSetFileType(file.id, e.target.value as ImportFileType)}
                  // KML/GTFS import straight from their geometry; the type is display-only
                  disabled={file.detectedType === 'gtfs' || file.detectedType === 'kml'}
                >
                  {FILE_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton
                size="small"
                onClick={() => onRemoveFile(file.id)}
                aria-label={t('importWizard.uploadStep.removeFile')}
              >
                <Icon name="xmark" size={16} />
              </IconButton>
            </Box>
          ))}

          {files.some(f => f.parseError) && (
            <Alert severity="error">
              {files.filter(f => f.parseError).map(f => (
                <div key={f.id}><strong>{f.fileName}:</strong> {f.parseError}</div>
              ))}
            </Alert>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default FileUploadStep;
